/*
 * Entry point for the console's client code. Boots the plumbing (action
 * dispatch, lazy tabs, navigation history, the rail, content enhancers, the
 * UTC time bridge) and registers every area.
 *
 * Loaded as a module (deferred): it runs after every classic script in the
 * page and before DOMContentLoaded. The classic scripts that load ahead of it
 * from /_content/Mcp.Core.Web -- theme.js and auth-refresh.js -- are the reason
 * window.mcpAdmin.toast below exists; see its comment.
 *
 * The area registrations themselves live in areas/register.ts so a test can
 * apply them without also running this file's boot side effects.
 */
import { initDispatch } from './core/dispatch';
import { autoOpenTabs, initTabs } from './core/tabs';
import { initNav } from './core/nav';
import { initRail } from './core/rail';
import { initEnhancers, registerEnhancer } from './core/enhance';
import { initHints } from './core/hints';
import { enhanceAutoSubmitForms, enhanceDistinctSelectGroups, enhanceSelectSync, initTimeOfDay } from './core/forms';
import { enhanceAutocompletes } from './components/combobox';
import { initEntityPicker } from './components/entity-picker';
import { registerAllAreas } from './areas/register';
import { toast } from './core/toast';
import { enhanceValidatedForms } from './core/validation';
import { enhancePasswordReveal } from './core/password-reveal';
import { enhanceResourceUriPreview, initApplicationArea } from './areas/application';
import { enhanceResourceForms, initResourceArea } from './areas/resource';
import { enhancePolicyForms } from './areas/resource-policy';
import { enhanceClaimTypeForm } from './areas/claim-type';
import { enhanceThemePanel } from './areas/claim-template';
import { enhanceBinderTab } from './areas/license-binder';
import { enhanceQuotaTab } from './areas/license-quota';
import { enhanceTemplateQuotaForm } from './areas/license-template';
import { enhanceFeatureTabs, initFeatureArea } from './areas/feature';
import { enhanceStageForms } from './areas/feature-rollout';
import { enhanceCertificateImport } from './areas/certificate';
import { initSamlPeerArea } from './areas/samlpeer';
import { enhanceChangePasswordForm, initThemeSync } from './areas/home';
import { enhancePolicyTimeWindow } from './areas/person-policy';
import { enhanceExportTemplateDesigner } from './areas/export-template';
import { enhanceImportRows, initImportArea } from './areas/import';

registerAllAreas();

registerEnhancer(initHints);
// Forms that asked for validation in markup (data-mcp-validate). The anonymous
// account pages are the callers: they are plain full-page posts and used to get
// this from jquery.validate.unobtrusive's document-wide scan on ready.
registerEnhancer(enhanceValidatedForms);
// Show/hide toggle on every password box (sign-up, invite, reset, change
// password, the step-up modal, admin password fields).
registerEnhancer(enhancePasswordReveal);
// A partial that must drive a top-level navigation says so in markup
// (data-mcp-autosubmit) rather than shipping an inline <script>.
registerEnhancer(enhanceAutoSubmitForms);
// Both challenge-question pickers carried the same inline de-dup script;
// they opt in with data-mcp-distinct-group now. The dashboard points the
// rail's domain picker at itself with data-mcp-set-select.
registerEnhancer(enhanceDistinctSelectGroups);
registerEnhancer(enhanceSelectSync);
registerEnhancer(enhanceAutocompletes);
registerEnhancer(autoOpenTabs);
// Seeds the Resource URI preview wherever a Resource form lands -- first
// paint, an AJAX swap or a modal.
registerEnhancer(enhanceResourceForms);
registerEnhancer(enhanceResourceUriPreview);
registerEnhancer(enhancePolicyForms);
// The Claim Type category rule / IncludeInToken warning and the Claim Template
// theme panel were each initialised from whichever handler happened to open
// their modal, so they were silent on every other path in.
registerEnhancer(enhanceClaimTypeForm);
registerEnhancer(enhanceThemePanel);
// The License Detail tabs. Each of these used to be a document-level jQuery
// change handler that only ran on a change the OPERATOR made, so a freshly
// rendered tab showed rules it had not applied yet -- Block offered on a
// counter nothing can count, a Rate row with its Period control hidden.
registerEnhancer(enhanceBinderTab);
registerEnhancer(enhanceQuotaTab);
registerEnhancer(enhanceTemplateQuotaForm);
// The Feature Detail modal: its lazy tabs need the domain the rail is pointed
// at stamped onto their URLs, and a stage form's trigger rule has to run on
// arrival -- the Rollout tab re-renders itself after every write.
registerEnhancer(enhanceFeatureTabs);
registerEnhancer(enhanceStageForms);
// The Certificate Import dialog. The server re-renders this same partial after
// a refusal (unreadable body, unfit purpose, the validity confirmation), and
// the legacy code had to re-run its Init() by hand on exactly that one path.
registerEnhancer(enhanceCertificateImport);
// Manage Account: clear the browser's autofilled password and take over Enter.
// This used to be an inline <script> at the foot of the view, which never ran
// when the partial was swapped into the console shell.
registerEnhancer(enhanceChangePasswordForm);
// The Person Policy login window. Starting it from whichever handler opened
// the dialog meant a form reached by any other path showed an unticked "no
// restriction" box over two empty, enabled time inputs.
registerEnhancer(enhancePolicyTimeWindow);
// The Export Template designer. Its initialiser was an inline <script> in the
// modal partial, and a modal body is placed with innerHTML -- which never runs
// an inserted script -- so the designer never hydrated at all.
registerEnhancer(enhanceExportTemplateDesigner);
// The import session's row table: paging and filtering are laid out after
// every render of the panel, including a tab load.
registerEnhancer(enhanceImportRows);

// The one name this module publishes on window. auth-refresh.js is a CLASSIC
// script from Mcp.Core.Web that loads BEFORE this module -- deliberately, so it
// is still alive if the module 404s or throws, and two of the messages it
// carries say the user's change was not applied. _Layout's window.mcpSession
// .toast hook forwards to this; it returns false when we are not here, and
// auth-refresh falls back to its own banner.
(window.mcpAdmin ??= {}).toast = toast;

initApplicationArea();
initResourceArea();
initFeatureArea();
initSamlPeerArea();
initThemeSync();
initImportArea();
initDispatch();
initEntityPicker();
initTabs();
initNav();
initRail();
initEnhancers();
initTimeOfDay();
